import {
    type IsoAndUnixTimestamp,
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { snakeCase } from "@@/utilities/casing";
import { sum } from "@@/utilities/sum";
import type { Except } from "type-fest";
import { z } from "zod";
import { assertNever } from "../assert-never";
import {
    type CurrencyCode,
    currencyCodeZodSchema,
} from "../currencies/currencies";
import { type DeliveryMethodType } from "../deliveries/delivery-methods";
import {
    type Percentage,
    type PercentageKey,
    percentageZodSchema,
    roundPercentage,
    toPercentageFromPercentageKey,
    toPercentageKey,
} from "../percentage";
import {
    type Translatable,
    translatableZodSchema,
} from "../translations/translation";
import {
    type PriceId,
    isPriceId,
    priceIdFactory,
    priceIdZodSchema,
} from "./price-id";

type Price = {
    readonly _id: PriceId;
    readonly _type: "PRICE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly tags: string[];
    readonly amountExcludingVat: number;
    readonly amountIncludingVat: number;
    readonly vat: Percentage;
    /**
     * All new prices (summer 2022 and forward) have this property
     * @type {Percentage}
     */
    readonly vatRounded?: Percentage;
    readonly vatAmount: number;
    readonly vatAmounts?: AmountsByVatRate | null;
    readonly prefix?: Translatable;
    readonly currencyCode: CurrencyCode;
};

type AmountsAndVatRate = {
    amountExcludingVat: number;
    amountIncludingVat: number;
    vatAmount: number;
    vatRate: Percentage;
};
type AmountsByVatRate = Record<PercentageKey, AmountsAndVatRate>;

const amountsByVatRateZodSchema: z.ZodSchema<AmountsByVatRate> = z.record(
    z.string(),
    z.object({
        amountExcludingVat: z.number(),
        amountIncludingVat: z.number(),
        vatAmount: z.number(),
        vatRate: percentageZodSchema,
    }),
);

const priceZodSchema: z.ZodSchema<Price> = z
    .object({
        _id: priceIdZodSchema,
        _type: z.literal("PRICE"),
        _createdAt: isoAndUnixZodSchema,
        tags: z.array(z.string()),
        amountExcludingVat: z.number(),
        amountIncludingVat: z.number(),
        vat: percentageZodSchema,
        vatRounded: percentageZodSchema.optional(),
        vatAmount: z.number(),
        vatAmounts: amountsByVatRateZodSchema.nullish(),
        prefix: translatableZodSchema.optional(),
        currencyCode: currencyCodeZodSchema,
    })
    .refine(price => {
        return (
            price.amountExcludingVat ===
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                Math.round(price.amountIncludingVat / (1 + price.vat)),
            { message: "amount excluding vat is not correct" }
        );
    })
    .refine(price => {
        return (
            price.vatAmount ===
                price.amountIncludingVat - price.amountExcludingVat,
            { message: "vat amount is not correct" }
        );
    });

const priceAsStringForComparison = (price: Price): string => {
    return `{price.amountExcludingVat}${price.amountIncludingVat}${price.currencyCode}${price.vat}${price.vatAmount}`;
};

const priceCompare = (price1: Price, price2: Price): boolean => {
    return (
        priceAsStringForComparison(price1) ===
        priceAsStringForComparison(price2)
    );
};

type Term = {
    vatAmount: number;
    amountExcludingVat: number;
    amountIncludingVat: number;
    currencyCode: CurrencyCode;
    vatAmounts?: AmountsByVatRate | null;
};

const zeroTerm = (currencyCode: CurrencyCode = "SEK"): Term => ({
    vatAmount: 0,
    amountExcludingVat: 0,
    amountIncludingVat: 0,
    currencyCode,
    vatAmounts: undefined,
});

const termFromPrice = (price: Price): Term => ({
    vatAmount: price.vatAmount,
    amountExcludingVat: price.amountExcludingVat,
    amountIncludingVat: price.amountIncludingVat,
    currencyCode: price.currencyCode,
    vatAmounts: price.vatAmounts,
});

type PriceFactoryParams1 = {
    readonly _id?: PriceId;
    readonly tags?: string[];
    readonly vat?: Percentage | number;
    readonly amountIncludingVat: number;
    readonly currencyCode?: CurrencyCode;
};
type PriceFactoryParams1_5 = {
    readonly _id?: PriceId;
    readonly tags?: string[];
    readonly vat?: Percentage | number;
    readonly amountExcludingVat: number;
    readonly currencyCode?: CurrencyCode;
};
type PriceFactoryParams2 = {
    readonly _id?: PriceId;
    readonly tags?: string[];
    readonly vat?: Percentage | number;
    readonly amountIncludingVat: number;
    readonly amountExcludingVat: number;
    readonly vatAmount: number;
    readonly vatAmounts?: AmountsByVatRate;
    readonly currencyCode?: CurrencyCode;
};
type PriceFactoryParams3 = {
    readonly _id?: PriceId;
    readonly tags?: string[];
    readonly vat?: Percentage | number;
    readonly amountExcludingVat: number;
    readonly currencyCode?: CurrencyCode;
};
type PriceFactoryParams4 = {
    readonly _id?: PriceId;
    readonly tags?: string[];
    vatAmounts: Record<
        PercentageKey,
        Pick<AmountsAndVatRate, "vatAmount" | "amountIncludingVat">
    >;
    readonly currencyCode?: CurrencyCode;
};

const _isFactoryParams1 = (
    params: PriceFactoryParams1 | PriceFactoryParams3 | PriceFactoryParams4,
): params is PriceFactoryParams1 => {
    if (_isFactoryParams2(params)) return false;

    return "amountIncludingVat" in params && !("vatAmounts" in params);
};
const _isFactoryParams2 = (
    params:
        | PriceFactoryParams1
        | PriceFactoryParams2
        | PriceFactoryParams3
        | PriceFactoryParams4,
): params is PriceFactoryParams2 => {
    return "vatAmount" in params;
};
const _isFactoryParams3 = (
    params:
        | PriceFactoryParams1
        | PriceFactoryParams2
        | PriceFactoryParams3
        | PriceFactoryParams4,
): params is PriceFactoryParams3 => {
    if (_isFactoryParams2(params)) return false;
    return "amountExcludingVat" in params;
};
const _isFactoryParams4 = (
    params:
        | PriceFactoryParams1
        | PriceFactoryParams2
        | PriceFactoryParams3
        | PriceFactoryParams4,
): params is PriceFactoryParams4 => {
    return (
        !("amountIncludingVat" in params) &&
        "vatAmounts" in params &&
        typeof params.vatAmounts !== "undefined" &&
        !!Object.keys(params.vatAmounts).length
    );
};

/**
 * Remember, values in `ÖRE` if `SEK` or corresponding lowest unit
 * @param {number} amount (including vat)
 * @param {number} vat defaults to 25% (0.25)
 * @param {string} [currencyCode="SEK"] defaults to SEK
 */
function priceFactory(params: PriceFactoryParams1): Price;
function priceFactory(params: PriceFactoryParams2): Price;
function priceFactory(params: PriceFactoryParams3): Price;
function priceFactory(params: PriceFactoryParams4): Price;
function priceFactory(params: Price): Price;
function priceFactory(
    params:
        | PriceFactoryParams1
        | PriceFactoryParams2
        | PriceFactoryParams3
        | PriceFactoryParams4
        | Price,
): Price {
    const tags = params.tags?.map(snakeCase) ?? [];
    const currencyCode: CurrencyCode = params.currencyCode ?? "SEK";
    // console.log("💰", params);

    if ("_type" in params && params._type === "PRICE") {
        return priceFactory({
            amountIncludingVat: params.amountIncludingVat,
            amountExcludingVat: params.amountExcludingVat,
            vatAmount: params.vatAmount,
            currencyCode: params.currencyCode,

            tags: [...params.tags],
            vat: params.vat,
            vatAmounts: params.vatAmounts ? params.vatAmounts : undefined,
        });
    }

    if (_isFactoryParams1(params)) {
        const vatPercentage: Percentage = percentageZodSchema.parse(
            params.vat ?? 0.25,
        );
        const vatPercentageRounded = roundPercentage(vatPercentage);
        const { amountIncludingVat: amountIncludingVatInput } = params;
        const amountIncludingVat = Math.round(amountIncludingVatInput);
        const amountExcludingVat = Math.round(
            amountIncludingVat / (1 + vatPercentage),
        );
        return {
            _id: isPriceId(params._id) ? params._id : priceIdFactory(),
            _type: "PRICE",
            _createdAt: nowIsoAndUnix(),
            amountExcludingVat,
            amountIncludingVat,
            vat: vatPercentage,
            vatRounded: vatPercentageRounded,
            tags,
            vatAmount: amountIncludingVat - amountExcludingVat,
            currencyCode,
            prefix: null,
            vatAmounts: undefined,
        };
    }
    if (_isFactoryParams4(params)) {
        const vatAmount =
            sum(Object.values(params.vatAmounts).map(item => item.vatAmount)) ||
            0;
        const amountIncludingVat = Math.round(
            sum(
                Object.values(params.vatAmounts).map(
                    item => item.amountIncludingVat,
                ),
            ) || 0,
        );
        const vatPercentage: Percentage =
            Object.keys(params.vatAmounts).length === 1
                ? toPercentageFromPercentageKey(
                      Object.keys(params.vatAmounts)[0],
                  )
                : percentageZodSchema.parse(
                      vatAmount / (amountIncludingVat - vatAmount),
                  );
        const vatPercentageRounded = roundPercentage(vatPercentage);
        const amountExcludingVat = Math.round(
            amountIncludingVat / (1 + vatPercentage),
        );
        // console.log("🤔", params, {
        //     vatAmount,
        //     vatAmounts: params.vatAmounts,
        //     vatPercentage,
        //     vatPercentageRounded,
        //     amountIncludingVat,
        //     amountExcludingVat,
        // });
        return {
            _id: isPriceId(params._id) ? params._id : priceIdFactory(),
            _type: "PRICE",
            _createdAt: nowIsoAndUnix(),
            amountExcludingVat,
            amountIncludingVat,
            vat: vatPercentage,
            vatRounded: vatPercentageRounded,
            tags,
            vatAmount: amountIncludingVat - amountExcludingVat,
            vatAmounts: Object.entries(params.vatAmounts).reduce(
                (acc, [key, value]) => {
                    return {
                        ...acc,
                        [key]: {
                            amountIncludingVat: value.amountIncludingVat,
                            amountExcludingVat:
                                value.amountIncludingVat - value.vatAmount,
                            vatAmount: value.vatAmount,
                            vatRate: toPercentageFromPercentageKey(key),
                        },
                    };
                },
                {} as AmountsByVatRate,
            ),
            currencyCode,
            prefix: null,
        };
    }

    if (_isFactoryParams2(params)) {
        const vatPercentage =
            params.amountExcludingVat === 0 &&
            params.amountIncludingVat === 0 &&
            params.vatAmount === 0
                ? (0 as Percentage)
                : //percentageZodSchema.parse(
                  (params.vatAmount && params.vat) ||
                  Math.abs(params.vatAmount) /
                      Math.abs(params.amountExcludingVat);
        //);

        const vatPercentageRounded = roundPercentage(
            vatPercentage as Percentage,
        );

        // Validate price amounts
        const amountsAreZero =
            params.amountExcludingVat === 0 &&
            params.amountIncludingVat === 0 &&
            params.vatAmount === 0;
        const amountsAreRelativelyValid =
            params.amountIncludingVat - params.amountExcludingVat ===
            params.vatAmount;
        const vatPercentageValid =
            !amountsAreZero &&
            // Rounded percentage is correct
            // We don't want to care about the exact decimals so we make
            // sure it's in the ballpark just validating the rounded value
            roundPercentage(
                (Math.abs(params.vatAmount) /
                    Math.abs(params.amountExcludingVat)) as Percentage,
            ) === vatPercentageRounded;

        if (
            !amountsAreZero &&
            (!amountsAreRelativelyValid || !vatPercentageValid)
        ) {
            throw new Error(
                `vatAmount is not correct; incl: ${
                    params.amountIncludingVat
                }, excl: ${params.amountExcludingVat}, vat: ${
                    params.vatAmount
                }, ${vatPercentage * 100} %`,
            );
        }

        return {
            _id: isPriceId(params._id) ? params._id : priceIdFactory(),
            _type: "PRICE",
            _createdAt: nowIsoAndUnix(),
            amountExcludingVat: params.amountExcludingVat,
            amountIncludingVat: params.amountIncludingVat,
            vat: vatPercentage as Percentage,
            vatRounded: vatPercentageRounded,
            tags,
            vatAmount: params.vatAmount,
            vatAmounts: params.vatAmounts ? params.vatAmounts : undefined,
            currencyCode,
            prefix: null,
        };
    }

    if (_isFactoryParams3(params)) {
        const vatPercentage: Percentage = percentageZodSchema.parse(
            params.vat ?? 0.25,
        );
        const { amountExcludingVat: amountExcludingVat } = params;
        //    const amountExcludingVat = Math.round(amountExcludingVat);

        const amountIncludingVat = Math.round(
            amountExcludingVat * (1 + vatPercentage),
        );
        return {
            _id: isPriceId(params._id) ? params._id : priceIdFactory(),
            _type: "PRICE",
            _createdAt: nowIsoAndUnix(),
            amountExcludingVat,
            amountIncludingVat,
            vat: vatPercentage,
            tags,
            vatAmount: amountIncludingVat - amountExcludingVat,
            currencyCode,
            prefix: null,
        };
    }
    assertNever(params);
}

const createPriceFromExcludingVat = (
    props: Except<PriceFactoryParams1, "amountIncludingVat" | "vat"> & {
        amountExcludingVat: number;
        vat: Percentage;
    },
) => {
    return priceFactory({
        amountIncludingVat: props.amountExcludingVat * (1 + props.vat),
        currencyCode: "SEK",
        vat: props.vat,
    });
};

/**
 * E.g. for `SEK` set a price in `kronor` and not `öre` \
 * price is then converted to `öre`
 * @param {PriceFactoryParams1} params
 * @return {*}  {Price}
 */
function priceForHumansFactory(params: PriceFactoryParams1): Price;
function priceForHumansFactory(params: PriceFactoryParams1_5): Price;
function priceForHumansFactory(params: PriceFactoryParams4): Price;
function priceForHumansFactory(
    params: PriceFactoryParams1 | PriceFactoryParams1_5 | PriceFactoryParams4,
): Price {
    if (_isFactoryParams4(params)) {
        return priceFactory({
            ...params,
            vatAmounts: Object.entries(params.vatAmounts).reduce(
                (acc, [key, data]) => ({
                    ...acc,
                    [key]: {
                        vatAmount: data.vatAmount * 100,
                        amountIncludingVat: data.amountIncludingVat * 100,
                    },
                }),
                {},
            ),
        });
    }

    // Is amount provided excl. vat?
    if ("amountExcludingVat" in params) {
        return priceFactory({
            ...params,
            amountExcludingVat: params.amountExcludingVat * 100,
        });
    }

    // or incl. vat?
    return priceFactory({
        ...params,
        amountIncludingVat: params.amountIncludingVat * 100,
    });
}
const legacyCustomerServiceFeePrice = priceForHumansFactory({
    amountIncludingVat: 3,
    vat: 0.25 as Percentage,
    currencyCode: "SEK",
});
const endCustomerServiceFee = (
    deliveryMethodType: DeliveryMethodType,
): Price => {
    switch (deliveryMethodType) {
        case "PICK_UP":
        case "HOME_DELIVERY":
            return legacyCustomerServiceFeePrice;
        case "TABLE_SERVICE":
            return zeroPrice;
        default:
            return zeroPrice;
    }
};

const zeroPrice: Price = priceForHumansFactory({
    amountIncludingVat: 0,
    vat: 0 as Percentage,
    currencyCode: "SEK",
});
const zeroPrice_25PercentVat: Price = priceForHumansFactory({
    amountIncludingVat: 0,
    vat: 0.25 as Percentage,
    currencyCode: "SEK",
});

const duplicatePrice = (price: Price): Price => {
    return {
        ...price,
        _id: priceIdFactory(),
    };
};

const isPriceEqual = (
    price1: Price | null | undefined,
    price2: Price | null | undefined,
) => {
    if (!price1 || !price2) return false;
    return (
        price1.amountIncludingVat === price2.amountIncludingVat &&
        price1.vat === price2.vat &&
        price1.currencyCode === price2.currencyCode
    );
};

function sortAmountsByVatRate(price: Price): AmountsByVatRate;
function sortAmountsByVatRate(price: undefined): undefined;
function sortAmountsByVatRate(
    price: Price | undefined,
): AmountsByVatRate | undefined;
function sortAmountsByVatRate(
    price: Price | undefined,
): AmountsByVatRate | undefined {
    if (!price) return undefined;
    return price.vatAmounts && Object.keys(price.vatAmounts).length
        ? price.vatAmounts
        : {
              [toPercentageKey(price.vat)]: {
                  amountExcludingVat: price.amountExcludingVat,
                  amountIncludingVat: price.amountIncludingVat,
                  vatAmount: price.vatAmount,
                  vatRate: price.vat,
              },
          };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPrice = (price: any): price is Price => {
    return price._type === "PRICE";
};

export {
    amountsByVatRateZodSchema,
    createPriceFromExcludingVat,
    duplicatePrice,
    endCustomerServiceFee,
    isPrice,
    isPriceEqual,
    legacyCustomerServiceFeePrice,
    priceAsStringForComparison,
    priceCompare,
    priceFactory,
    priceForHumansFactory,
    priceZodSchema,
    sortAmountsByVatRate,
    termFromPrice,
    zeroPrice,
    zeroPrice_25PercentVat,
    zeroTerm,
};
export type { AmountsAndVatRate, AmountsByVatRate, Price, Term as PriceTerm };
